import { Injectable } from '@angular/core';

import { RewardTokenType } from '../../../../lib/tokens-base/token-base.models';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { IBetslipPickState } from '../../picks/state';
import { IRewardToken } from '../../reward-tokens/reward-tokens.model';
import { getSelectedToken } from '../../reward-tokens/services/linear-reward-tokens.utils';
import { isFreebetToken } from '../../reward-tokens/services/reward-tokens.utils';
import { IRewardTokensState } from '../../reward-tokens/state';
import { IBetslipTypeState } from '../../types/state';
import { BetslipError } from '../../validation/errors/betslip-error';
import { getSlipErrorsForType } from '../../validation/services/utils/betslip-errors-state-utils';
import {
    isBetslipStatusInsufficientMoney,
    isErrorComboPrevention,
    isPickStatusLocked,
    isPickStatusOddsChanged,
} from '../../validation/services/utils/betslip-errors-utils';
import { IBetslipErrorsState } from '../../validation/state';
import { IBetslipTeaserErrorState } from '../models';

@Injectable()
export class TeaserBetErrorStateService {
    getTeaserErrorState(
        errors: IBetslipErrorsState,
        types: IBetslipTypeState,
        picks: IBetslipPickState,
        rewardTokens: IRewardTokensState,
    ): IBetslipTeaserErrorState {
        const teaser = types.teaserBet;
        let placeBlocking = false;
        let oddsChanged = false;
        let hasComboPrevention = false;
        let lockedPicksCount = 0;
        let hasLivePicks = false;
        let hasPickWithoutPrice = false;
        let picksErrorNumber = 0;
        let oddsAccepted = false;
        const isStakeEmpty = !teaser.stake;
        let hasRewardError = false;
        const teaserPicks: BetslipV1Pick[] = [];

        const selectedToken = getSelectedToken(rewardTokens.tokens, types.teaserBet);
        const isFreeBetSelected = isFreebetToken(selectedToken);
        const selectedPicksCount = Object.values(teaser.picks).reduce((count, pick) => {
            return count + (pick.isSelected ? 1 : 0);
        }, 0);

        Object.keys(teaser.picks).forEach((pickId) => {
            const pick = teaser.picks[pickId];
            placeBlocking = placeBlocking || !pick.selectedOption;
            if (pick.selectedOption) {
                const teaserPick = picks.pickList.find((x) => x.id.toString() === pickId) as BetslipV1Pick;
                if (teaserPick) {
                    teaserPicks.push(teaserPick);
                    hasLivePicks = hasLivePicks || teaserPick.isLive;

                    const pickErrors = errors.pickErrors[BetslipType.Teaser][teaserPick.id.toString()] || [];

                    oddsChanged = oddsChanged || pickErrors.some(isPickStatusOddsChanged);
                    placeBlocking = placeBlocking || pickErrors.some(this.isPlaceBlockingError);
                    lockedPicksCount += pickErrors.some(isPickStatusLocked) ? 1 : 0;
                    hasComboPrevention = hasComboPrevention || pickErrors.some(isErrorComboPrevention);
                    hasPickWithoutPrice = hasPickWithoutPrice || !teaserPick.currentPrice;
                    picksErrorNumber += pickErrors.length;
                    oddsAccepted = oddsAccepted || teaserPick.getOddsAcceptance();
                }
            }
        });

        hasRewardError = this.checkRewardTokenError(rewardTokens, selectedToken);

        const checkHasPlaceBlockingError = () =>
            [...errors.betslipErrors, ...getSlipErrorsForType(BetslipType.Teaser, errors.slipErrors, types, false)].some((e) =>
                this.isPlaceBlockingError(e),
            );

        placeBlocking = placeBlocking || selectedPicksCount < 2 || hasRewardError || checkHasPlaceBlockingError();

        return {
            placeBlocking,
            oddsChanged,
            oddsAccepted,
            lockedPicksCount,
            hasLivePicks,
            hasComboPrevention,
            picksErrorNumber,
            isStakeEmpty: isStakeEmpty && !isFreeBetSelected,
            hasPickWithoutPrice,
            hasRewardError,
        };
    }

    private isPlaceBlockingError(error: BetslipError): boolean {
        return !isPickStatusLocked(error) && !isPickStatusOddsChanged(error) && !isBetslipStatusInsufficientMoney(error) && error.hasClientValidation;
    }

    private checkRewardTokenError(rewardTokens: IRewardTokensState, selectedToken: IRewardToken | null): boolean {
        if (!rewardTokens?.tokens) {
            return false;
        }

        if (selectedToken && selectedToken.rewardTokenType !== RewardTokenType.FreeBet) {
            return true;
        }

        const eligibilityState = rewardTokens.eligibilityState.teaserBet.find((s) => s.tokenId === selectedToken?.userTokenId);

        if (!eligibilityState) {
            return false;
        }

        return !eligibilityState?.isEligible || Object.values(eligibilityState.softCriteriasValidity).some((v) => !v);
    }
}
